import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import socketService from "../services/socketService";

const ACK_TIMEOUT_MS = 12000;

const sortPatients = (patients = []) =>
  [...patients].sort(
    (left, right) => new Date(left.joinedAt) - new Date(right.joinedAt)
  );

const emitWaitingRoomWithAck = (event, payload) =>
  new Promise((resolve) => {
    let settled = false;

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({
        message: "Request timed out. Please try again.",
        success: false,
      });
    }, ACK_TIMEOUT_MS);

    socketService.emitWaitingRoom(event, payload, (response) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(response || { message: "No response from server.", success: false });
    });
  });

const useWaitingRoomNotifications = (appointmentId) => {
  const [waitingPatients, setWaitingPatients] = useState([]);
  const [visiblePatientId, setVisiblePatientId] = useState(null);
  const [isQueuePanelOpen, setIsQueuePanelOpen] = useState(false);
  const [isAdmitting, setIsAdmitting] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  const showNotificationForPatient = useCallback((patientId) => {
    if (!patientId) return;
    setVisiblePatientId(patientId);
    setIsQueuePanelOpen(false);
  }, []);

  const pickNextVisiblePatient = useCallback(
    (patients, excludePatientId = null) => {
      const nextPatient = patients.find(
        (patient) => patient.patientId !== excludePatientId
      );
      setVisiblePatientId(nextPatient?.patientId ?? null);
    },
    []
  );

  const updateWaitingPatients = useCallback((patients) => {
    const sortedPatients = sortPatients(patients);
    setWaitingPatients(sortedPatients);

    setVisiblePatientId((currentVisiblePatientId) => {
      if (
        currentVisiblePatientId &&
        sortedPatients.some(
          (patient) => patient.patientId === currentVisiblePatientId
        )
      ) {
        return currentVisiblePatientId;
      }

      return sortedPatients[0]?.patientId ?? null;
    });
  }, []);

  const handleDismiss = useCallback(
    async (patientId) => {
      if (!appointmentId || !patientId || isDismissing) {
        return false;
      }

      setIsDismissing(true);

      try {
        const response = await emitWaitingRoomWithAck("waiting-room:dismiss", {
          appointmentId,
          patientId,
        });

        if (!response?.success) {
          toast.error(response.message || "Unable to dismiss patient.");
          return false;
        }

        const nextPatients = response.waitingPatients || [];
        updateWaitingPatients(nextPatients);
        pickNextVisiblePatient(nextPatients, patientId);
        return true;
      } finally {
        setIsDismissing(false);
      }
    },
    [
      appointmentId,
      isDismissing,
      pickNextVisiblePatient,
      updateWaitingPatients,
    ]
  );

  const handleAdmit = useCallback(
    async (patientId) => {
      if (!appointmentId || !patientId || isAdmitting) {
        return false;
      }

      setIsAdmitting(true);

      try {
        const response = await emitWaitingRoomWithAck("waiting-room:admit", {
          appointmentId,
          patientId,
        });

        if (!response?.success) {
          toast.error(response.message || "Unable to admit patient.");
          return false;
        }

        const nextPatients = response.waitingPatients || [];
        updateWaitingPatients(nextPatients);
        pickNextVisiblePatient(nextPatients, patientId);
        return true;
      } finally {
        setIsAdmitting(false);
      }
    },
    [
      appointmentId,
      isAdmitting,
      pickNextVisiblePatient,
      updateWaitingPatients,
    ]
  );

  useEffect(() => {
    if (!appointmentId) return undefined;

    socketService.connectWaitingRoom();
    const socket = socketService.getWaitingRoomSocket();

    const joinAsProvider = () => {
      socketService.emitWaitingRoom(
        "waiting-room:provider-join",
        { appointmentId },
        (response) => {
          if (response?.success) {
            updateWaitingPatients(response.waitingPatients || []);
          }
        }
      );
    };

    const handlePatientJoined = ({ patient, waitingPatients: patients }) => {
      const sortedPatients = sortPatients(patients);
      updateWaitingPatients(sortedPatients);

      if (patient?.patientId) {
        showNotificationForPatient(patient.patientId);
      }
    };

    const handlePatientLeft = ({ patientId, waitingPatients: patients }) => {
      updateWaitingPatients(patients);

      setVisiblePatientId((currentVisiblePatientId) => {
        if (currentVisiblePatientId !== patientId) {
          return currentVisiblePatientId;
        }

        return patients[0]?.patientId ?? null;
      });
    };

    const handleQueueUpdated = ({ waitingPatients: patients }) => {
      updateWaitingPatients(patients);
    };

    const handlePatientAdmitted = ({ patientId, waitingPatients: patients }) => {
      updateWaitingPatients(patients);

      setVisiblePatientId((currentVisiblePatientId) => {
        if (currentVisiblePatientId !== patientId) {
          return currentVisiblePatientId;
        }
        return patients[0]?.patientId ?? null;
      });
    };

    const handleWaitingRoomError = ({ message }) => {
      if (message) {
        toast.error(message);
      }
    };

    socketService.onWaitingRoom("waiting-room:patient-joined", handlePatientJoined);
    socketService.onWaitingRoom("waiting-room:patient-left", handlePatientLeft);
    socketService.onWaitingRoom("waiting-room:queue-updated", handleQueueUpdated);
    socketService.onWaitingRoom(
      "waiting-room:patient-admitted",
      handlePatientAdmitted
    );
    socketService.onWaitingRoom("waiting-room:error", handleWaitingRoomError);

    joinAsProvider();
    socket?.on("connect", joinAsProvider);

    return () => {
      socket?.off("connect", joinAsProvider);
      socketService.offWaitingRoom(
        "waiting-room:patient-joined",
        handlePatientJoined
      );
      socketService.offWaitingRoom("waiting-room:patient-left", handlePatientLeft);
      socketService.offWaitingRoom(
        "waiting-room:queue-updated",
        handleQueueUpdated
      );
      socketService.offWaitingRoom(
        "waiting-room:patient-admitted",
        handlePatientAdmitted
      );
      socketService.offWaitingRoom("waiting-room:error", handleWaitingRoomError);
      socketService.disconnectWaitingRoom();
    };
  }, [appointmentId, showNotificationForPatient, updateWaitingPatients]);

  const visiblePatient = useMemo(
    () =>
      waitingPatients.find((patient) => patient.patientId === visiblePatientId) ||
      null,
    [visiblePatientId, waitingPatients]
  );

  const waitingCount = waitingPatients.length;
  const showWaitingIndicator =
    waitingCount > 0 && (!visiblePatient || !visiblePatientId);

  return {
    handleAdmit,
    handleDismiss,
    isAdmitting,
    isDismissing,
    isQueuePanelOpen,
    setIsQueuePanelOpen,
    showWaitingIndicator,
    visiblePatient,
    waitingCount,
    waitingPatients,
  };
};

export default useWaitingRoomNotifications;
